import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowDown,
  BookOpen,
  Clock,
  FileText,
  Loader2,
  RotateCcw,
  Save,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import AgreementsShell from "@/components/agreements/AgreementsShell";
import LegalDisclaimer, { AttorneyBadgeNote } from "@/components/agreements/LegalDisclaimer";
import CopyButton from "@/components/agreements/CopyButton";
import SaveDraftToProjectDialog from "@/components/agreements/SaveDraftToProjectDialog";
import { DOC_BY_ID } from "@/data/agreements/documentLibrary";
import { CATEGORY_BY_ID } from "@/data/agreements/categories";
import { templateForDoc } from "@/data/agreements/templateLibrary";
import { renderDocument } from "@/lib/agreements/renderDocTemplate";
import { useAgreementProject } from "@/hooks/useAgreementProject";
import { eventsApi } from "@/lib/agreements/api";
import { api } from "@/lib/api";

/**
 * TemplateView — route: /agreements/template/:docId
 *
 * The Founder Guide, then the template. The disclaimer sits above the document and is also
 * printed into every rendered copy by renderDocument() — neither is optional.
 */

const MULTILINE_TOKEN = /ADDRESS|DESCRIPTION|SCOPE|PURPOSE|SPECIFICATION|DELIVERABLE|SERVICES|TERRITORY|NOTICE ADDRESS/;
const isFilled = (v) => v !== undefined && v !== null && String(v).trim() !== "";

export default function TemplateView() {
  const { docId } = useParams();
  const [searchParams] = useSearchParams();
  const projectWrapperId = searchParams.get("project") || null;
  const { projectId, tokens, templateProfile, loading } = useAgreementProject();

  const doc = DOC_BY_ID[docId];
  const vendorTemplate = docId ? templateForDoc(docId) : null;
  // Admin-authored fallback for the ~208 docs that ship without a vendor
  // template. Fetched from /api/agreements/admin/merged/{docId}; vendor
  // templates short-circuit the fetch so nothing changes for the 49.
  const [adminTemplate, setAdminTemplate] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (!docId || vendorTemplate) { setAdminTemplate(null); return; }
    (async () => {
      try {
        const { data } = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/agreements/admin/merged/${docId}`,
          { headers: { Authorization: `Bearer ${localStorage.getItem("cpg.token") || localStorage.getItem("token") || ""}` } },
        ).then((r) => r.json().then((d) => ({ data: d })));
        if (!cancelled && data && data.source === "admin") {
          setAdminTemplate({
            docId,
            body: data.body,
            placeholders: (data.placeholders || []).map((p) => ({
              token: p.id || p.token,
              label: p.label,
              help: p.help,
              brandKey: p.brandKey,
            })),
          });
        }
      } catch { /* not-found is expected */ }
    })();
    return () => { cancelled = true; };
  }, [docId, vendorTemplate]);
  const template = vendorTemplate || adminTemplate;

  const [edits, setEdits] = useState({});
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  // Wrapper project this draft is attached to (null until first save,
  // or set on mount when the user opened a saved draft from Projects).
  // Keeping it in state lets subsequent "Save draft" clicks PATCH the
  // same wrapper so Projects never shows a duplicate card for the
  // same draft.
  const [attachedProjectId, setAttachedProjectId] = useState(projectWrapperId);
  const [attachedFolderId, setAttachedFolderId] = useState(null);
  const [attachedName, setAttachedName] = useState("");
  const [hydratingFromProject, setHydratingFromProject] = useState(!!projectWrapperId);

  // Rehydrate the form from a saved wrapper project when the Projects
  // page opens an existing draft (`/agreements/template/:docId?project=…`).
  useEffect(() => {
    if (!projectWrapperId) {
      setHydratingFromProject(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/projects/${encodeURIComponent(projectWrapperId)}`);
        if (cancelled) return;
        const snap = r.data?.agreements_draft_snapshot;
        if (snap && typeof snap === "object") {
          setEdits({ ...(snap.values || {}) });
        }
        setAttachedProjectId(r.data?.id || projectWrapperId);
        setAttachedFolderId(r.data?.parent_id || null);
        setAttachedName(r.data?.name || "");
      } catch {
        toast.error("Could not load that saved draft — starting a fresh copy of the template.");
        setAttachedProjectId(null);
      } finally {
        if (!cancelled) setHydratingFromProject(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectWrapperId]);

  // Reset the form when navigating between templates — but only when
  // not rehydrating from a saved wrapper project (the rehydration
  // effect above owns `edits` in that case).
  useEffect(() => {
    if (projectWrapperId) return;
    setEdits({});
    if (docId && templateForDoc(docId)) eventsApi.track({ type: "open_template", projectId, docId });
  }, [docId, projectId, projectWrapperId]);

  const placeholders = useMemo(() => template?.placeholders || [], [template]);
  const placeholderByToken = useMemo(
    () => Object.fromEntries(placeholders.map((p) => [p.token, p])),
    [placeholders],
  );

  /**
   * What the intake offers this template, keyed by token. Two routes lead here — the intake's own
   * token map, and each placeholder's declared `brandKey` — so read both, or a template whose
   * blank is only reachable by one of them silently disagrees with the form beside it.
   */
  const autofill = useMemo(() => {
    const out = {};
    for (const p of placeholders) {
      const fromToken = tokens?.[p.token];
      const fromProfile = p.brandKey ? templateProfile?.[p.brandKey] : undefined;
      const v = isFilled(fromToken) ? fromToken : fromProfile;
      if (isFilled(v)) out[p.token] = String(v);
    }
    return out;
  }, [placeholders, tokens, templateProfile]);

  /**
   * Effective token values: what the founder typed, otherwise the intake auto-fill.
   * An explicitly emptied blank is an instruction, not an absence — it must clear the blank in the
   * document too, or a founder who deletes a wrong company name still copies it out.
   */
  const values = useMemo(() => {
    const out = { ...autofill };
    for (const p of placeholders) {
      const edit = edits[p.token];
      if (edit === undefined) continue;
      if (isFilled(edit)) out[p.token] = String(edit);
      else delete out[p.token];
    }
    return out;
  }, [placeholders, autofill, edits]);

  // `values` is already the whole truth — auto-fill included — so the profile is not passed again;
  // passing it would let a cleared blank refill itself from the intake behind the founder's back.
  const rendered = useMemo(
    () => (template ? renderDocument(template, {}, values) : null),
    [template, values],
  );
  const completeness = rendered?.completeness || { total: 0, filled: 0, missing: [], percent: 0 };

  const autofilledCount = placeholders.filter(
    (p) => edits[p.token] === undefined && isFilled(autofill[p.token]),
  ).length;

  /* ── No entry at all ───────────────────────────────────────────────────── */
  if (!doc) {
    return (
      <AgreementsShell
        crumbs={[{ label: "Templates" }, { label: "Not found" }]}
        title="We do not have that document"
        subtitle="The link may be out of date, or the id may be misspelled."
      >
        <Card className="border-dashed">
          <CardContent className="p-8 text-sm text-muted-foreground">
            Nothing in the library matches <code className="rounded bg-muted px-1 py-0.5">{String(docId)}</code>.{" "}
            <Link className="underline" to="/agreements/library">
              Search the library
            </Link>{" "}
            or{" "}
            <Link className="underline" to="/agreements/finder">
              tell us what you are trying to do
            </Link>
            .
          </CardContent>
        </Card>
      </AgreementsShell>
    );
  }

  const category = CATEGORY_BY_ID[doc.category];
  const crumbs = [
    { label: "Library", to: "/agreements/library" },
    { label: doc.name, to: `/agreements/library/${doc.id}` },
    { label: "Template" },
  ];

  /* ── Entry exists but is guidance-only ─────────────────────────────────── */
  if (!template) {
    return (
      <AgreementsShell
        crumbs={crumbs}
        title={doc.name}
        subtitle="This one is guidance-only — there is no template to fill in."
        actions={
          <Button asChild variant="outline">
            <Link to={`/agreements/library/${doc.id}`}>
              <BookOpen className="mr-2 h-4 w-4" /> Read the full entry
            </Link>
          </Button>
        }
      >
        <Card className="border-pink-300 bg-pink-50">
          <CardContent className="p-5 text-sm leading-relaxed text-pink-900">
            <p className="flex items-center gap-2 font-semibold">
              <AlertTriangle className="h-4 w-4 text-pink-600" /> No template, on purpose
            </p>
            <p className="mt-2">
              We have not drafted a fill-in template for {doc.name}. Documents in this area turn on your entity, your
              state and the specific transaction, and a generic starting point would do more harm than good — it would
              read as finished when it is not.
            </p>
            <p className="mt-2">
              What we do have is the full entry: when you need it, what it does, the clauses to watch, what founders
              commonly get wrong, and what to ask for. Take that to counsel and you will spend less of their time.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button asChild className="bg-pink-700 text-white hover:bg-pink-800">
                <Link to={`/agreements/library/${doc.id}`}>Open the {doc.name} entry</Link>
              </Button>
              {category && (
                <Button asChild variant="outline">
                  <Link to={`/agreements/library#cat-${category.id}`}>More in {category.short}</Link>
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {doc.attorneyReview === "strongly-recommended" && (
          <AttorneyBadgeNote
            level="strongly-recommended"
            note={doc.attorneyNote || "This document should be prepared or reviewed by qualified counsel."}
            className="mt-4"
          />
        )}

        <div className="mt-4">
          <LegalDisclaimer />
        </div>
      </AgreementsShell>
    );
  }

  /* ── The template ──────────────────────────────────────────────────────── */
  const guide = template.founderGuide || {};
  const negotiate = Array.isArray(guide.negotiateCarefully) ? guide.negotiateCarefully : [];
  const redFlags = Array.isArray(guide.redFlags) ? guide.redFlags : [];

  // Name the draft after the counterparty when we have one — a founder ends up with several.
  const counterpartyToken = placeholders.find((p) => /COUNTERPART|OTHER PARTY/.test(p.token))?.token;
  const draftTitle = [doc.name, counterpartyToken ? values[counterpartyToken] : null].filter(Boolean).join(" — ");

  const onSaveDraft = () => {
    // Opens the Save-to-Projects picker. All persistence (create or
    // update of the wrapper project + snapshot) lives inside the
    // dialog so the Save Draft flow mirrors "Save to Project" from
    // the Document Plan page exactly.
    setSaveDialogOpen(true);
  };

  const onDraftSaved = (wrapperId) => {
    if (wrapperId) setAttachedProjectId(wrapperId);
    eventsApi.track({ type: "save_draft", projectId, docId: doc.id });
  };

  return (
    <AgreementsShell
      wide
      crumbs={crumbs}
      title={doc.name}
      subtitle={guide.useThisWhen}
      actions={
        <>
          <Button asChild variant="outline">
            <Link to={`/agreements/library/${doc.id}`}>
              <BookOpen className="mr-2 h-4 w-4" /> Full entry
            </Link>
          </Button>
          <CopyButton
            text={rendered.text}
            label="Copy document"
            onCopied={() => eventsApi.track({ type: "copy_template", projectId, docId: doc.id })}
          />
        </>
      }
    >
      {/* The disclaimer, above everything. */}
      <LegalDisclaimer variant="template" className="mb-5 border-2 p-4 text-sm" />

      {/* Founder Guide */}
      <section className="mb-6">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <h2 className="text-lg font-semibold">Founder guide</h2>
          {guide.timeToComplete && (
            <Badge variant="outline" className="gap-1 font-normal">
              <Clock className="h-3 w-3" /> {guide.timeToComplete}
            </Badge>
          )}
          {template.version && (
            <Badge variant="outline" className="font-normal">
              v{template.version}
            </Badge>
          )}
          {doc.attorneyReview === "strongly-recommended" && (
            <Badge className="border-red-300 bg-red-100 font-normal text-red-900 hover:bg-red-100">Attorney-led</Badge>
          )}
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          {guide.useThisWhen && (
            <Card>
              <CardContent className="p-4">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Use this when</h3>
                <p className="mt-1.5 text-sm leading-relaxed">{guide.useThisWhen}</p>
              </CardContent>
            </Card>
          )}
          {guide.whyItMatters && (
            <Card>
              <CardContent className="p-4">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Why it matters</h3>
                <p className="mt-1.5 text-sm leading-relaxed">{guide.whyItMatters}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {negotiate.length > 0 && (
          <Card className="mt-3">
            <CardContent className="p-4 sm:p-5">
              <h3 className="text-sm font-semibold">
                {negotiate.length === 5 ? "Five things to negotiate carefully" : "Things to negotiate carefully"}
              </h3>
              <ol className="mt-3 space-y-3">
                {negotiate.map((n, i) => (
                  <li key={n.point || i} className="flex gap-3">
                    <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-xs font-semibold text-white">
                      {i + 1}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-medium">{n.point}</span>
                      <span className="mt-0.5 block text-sm leading-relaxed text-muted-foreground">{n.detail}</span>
                    </span>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        )}

        {redFlags.length > 0 && (
          <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-4">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-red-900">
              <AlertTriangle className="h-4 w-4 text-red-600" /> Red flags — stop and get advice
            </h3>
            <ul className="mt-2 space-y-1.5">
              {redFlags.map((flag, i) => (
                <li key={i} className="flex gap-2 text-sm leading-relaxed text-red-900">
                  <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-red-500" />
                  <span>{flag}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* Fill it in + the live document */}
      <div className="grid gap-6 lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
        {/* Left: the blanks */}
        <div className="min-w-0">
          <Card>
            <CardContent className="p-4 sm:p-5">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Fill in the blanks</h2>
                <div className="flex items-center gap-2">
                  {Object.keys(edits).length > 0 && (
                    <Button type="button" size="sm" variant="ghost" onClick={() => setEdits({})}>
                      <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Reset
                    </Button>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="lg:hidden"
                    onClick={() =>
                      document.getElementById("rendered-document")?.scrollIntoView({ behavior: "smooth", block: "start" })
                    }
                  >
                    <ArrowDown className="mr-1.5 h-3.5 w-3.5" /> Document
                  </Button>
                </div>
              </div>

              {loading ? (
                <p className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading your intake answers…
                </p>
              ) : autofilledCount > 0 ? (
                <p className="mb-3 flex items-start gap-2 rounded-lg border border-indigo-200 bg-indigo-50 p-2.5 text-xs text-indigo-900">
                  <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-indigo-600" />
                  <span>
                    {autofilledCount} field{autofilledCount === 1 ? "" : "s"} pre-filled from your brand intake. Type over
                    anything that is wrong.
                  </span>
                </p>
              ) : (
                <p className="mb-3 text-xs text-muted-foreground">
                  <Link className="underline" to="/agreements/intake">
                    Complete the intake
                  </Link>{" "}
                  and your company name, address and state fill in here automatically.
                </p>
              )}

              {placeholders.length === 0 ? (
                <p className="text-sm text-muted-foreground">This template has no blanks to fill.</p>
              ) : (
                <div className="space-y-3.5">
                  {placeholders.map((p) => {
                    const fromIntake = edits[p.token] === undefined && isFilled(autofill[p.token]);
                    const value = edits[p.token] !== undefined ? edits[p.token] : (autofill[p.token] ?? "");
                    const multiline = MULTILINE_TOKEN.test(p.token);
                    const inputId = `ph-${p.token.replace(/[^A-Za-z0-9]+/g, "-").toLowerCase()}`;
                    return (
                      <div key={p.token}>
                        <div className="mb-1 flex flex-wrap items-center gap-1.5">
                          <Label htmlFor={inputId} className="text-sm">
                            {p.label || p.token}
                          </Label>
                          {fromIntake && (
                            <span className="rounded-full border border-indigo-200 bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700">
                              From your brand intake
                            </span>
                          )}
                        </div>
                        {multiline ? (
                          <Textarea
                            id={inputId}
                            rows={2}
                            value={value}
                            placeholder={p.example || p.token}
                            onChange={(e) => setEdits((prev) => ({ ...prev, [p.token]: e.target.value }))}
                          />
                        ) : (
                          <Input
                            id={inputId}
                            value={value}
                            placeholder={p.example || p.token}
                            onChange={(e) => setEdits((prev) => ({ ...prev, [p.token]: e.target.value }))}
                          />
                        )}
                        <p className="mt-1 font-mono text-[10px] text-muted-foreground">{p.token}</p>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right: the live document */}
        <div id="rendered-document" className="min-w-0 scroll-mt-4 lg:sticky lg:top-4 lg:self-start">
          {/* Disclaimer again, directly above the body — it also prints into the copied text. */}
          <LegalDisclaimer variant="template" className="mb-3" />

          <Card>
            <CardContent className="p-4 sm:p-5">
              {/* Completeness */}
              <div className="mb-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-semibold">
                    {completeness.filled} of {completeness.total} blank{completeness.total === 1 ? "" : "s"} filled
                  </p>
                  <span className="text-xs tabular-nums text-muted-foreground">{completeness.percent}%</span>
                </div>
                <Progress value={completeness.percent} className="mt-2 h-2" />
                {completeness.missing.length > 0 && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-xs text-muted-foreground">
                      {completeness.missing.length} still blank — see what is missing
                    </summary>
                    <ul className="mt-2 flex flex-wrap gap-1.5">
                      {completeness.missing.map((token) => (
                        <li
                          key={token}
                          className="rounded-md border border-pink-200 bg-pink-50 px-1.5 py-0.5 text-[11px] text-pink-900"
                        >
                          {placeholderByToken[token]?.label || token}
                        </li>
                      ))}
                    </ul>
                    <p className="mt-2 text-[11px] text-muted-foreground">
                      Unfilled blanks stay visible in the document on purpose, so nothing goes out looking finished when
                      it is not.
                    </p>
                  </details>
                )}
              </div>

              <div className="mb-3 flex flex-wrap gap-2">
                <CopyButton
                  text={rendered.text}
                  label="Copy full document"
                  size="sm"
                  onCopied={() => eventsApi.track({ type: "copy_template", projectId, docId: doc.id })}
                />
                <Button type="button" size="sm" variant="outline" onClick={onSaveDraft} disabled={hydratingFromProject}>
                  <Save className="mr-1.5 h-3.5 w-3.5" />
                  {attachedProjectId ? "Update draft" : "Save draft"}
                </Button>
              </div>

              <pre className="max-h-[70vh] overflow-auto whitespace-pre-wrap break-words rounded-lg bg-muted/50 p-3 font-mono text-[11.5px] leading-relaxed sm:p-4 sm:text-xs">
                {rendered.text}
              </pre>
            </CardContent>
          </Card>

          {doc.attorneyReview === "strongly-recommended" && (
            <AttorneyBadgeNote
              level="strongly-recommended"
              note={doc.attorneyNote || "Have counsel prepare or review this before you sign or send it."}
              className="mt-3"
            />
          )}
        </div>
      </div>

      <SaveDraftToProjectDialog
        open={saveDialogOpen}
        onOpenChange={setSaveDialogOpen}
        defaultName={attachedName || draftTitle}
        docId={doc.id}
        docName={doc.name}
        templateId={template.id}
        values={values}
        renderedText={rendered.text}
        existingProjectId={attachedProjectId}
        existingFolderId={attachedFolderId}
        onSaved={onDraftSaved}
      />
    </AgreementsShell>
  );
}
